// §6.3 / §5.5 / §6.4 — the house switcher, in its two modes.
//
//   • `overview` is Task View: a grid of thumbnails over a strip of desktops, opened and left
//     open, driven with the arrows.
//   • `switcher` is Alt-Tab: opened *while a modifier is held*, advanced by pressing the chord
//     again, and committed the moment that modifier comes back up.
//
// Cancelling must cost nothing in either. `Esc` closes and leaves focus exactly where it was,
// which is why nothing here focuses a window until it is committed — a switcher that focused as
// you moved would make "never mind" impossible.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernel, useStoreValue, useWindows } from '@/kernel/react';
import { modifiersOf } from '@/kernel/keyboard';
import { isGridKey, moveInGrid } from '@/kernel/wm/grid';
import { sortDesktops } from '@/kernel/wm/desktops';

/** How many thumbnails a row holds. Arrow-up and arrow-down move by this much. */
export const SWITCHER_COLUMNS = 4;

export function Switcher() {
  const kernel = useKernel();
  const overlay = useStoreValue(kernel.shellui.store, (s) => s.overlay);
  const open = overlay === 'switcher' || overlay === 'overview';
  // Alt-Tab mode. Task View is opened and left open; this one lives only while a key is down.
  const cycling = overlay === 'switcher';
  const allWindows = useWindows();
  const [selected, setSelected] = useState(0);
  const surface = useRef<HTMLDivElement | null>(null);

  // Subscribed, not read once: switching desktops from the strip has to re-filter the grid,
  // and nothing else in this component changes when it does.
  const activeDesktop = useStoreValue(kernel.wm.store, (s) => s.activeDesktopId);
  const desktopList = useStoreValue(kernel.wm.store, (s) => s.desktops);
  const desktops = useMemo(() => sortDesktops(desktopList), [desktopList]);

  // Most recently focused first: the window you want is nearly always the one you just left.
  const windows = useMemo(
    () =>
      allWindows
        .filter((win) => win.desktopId === activeDesktop)
        .slice()
        .sort((a, b) => b.focusedAt - a.focusedAt),
    [allWindows, activeDesktop],
  );

  useEffect(() => {
    if (!open) return;
    // Alt-Tab's first press lands on the window you were *last* in, not the one you are in.
    setSelected(cycling ? 1 : 0);
    surface.current?.focus();
  }, [open, cycling]);

  const commit = useCallback(
    (index: number) => {
      const win = windows[index];
      kernel.shellui.closeOverlay();
      if (!win) return;
      if (win.state === 'minimized') kernel.wm.restore(win.id);
      kernel.wm.focus(win.id);
    },
    [kernel, windows],
  );

  // The commit the modifier-release handler will run. Held in a ref so the listener can be
  // attached once per opening rather than re-attached on every selection change.
  const pending = useRef({ commit, selected });
  pending.current = { commit, selected };

  // §6.4 — advance on every further press of the chord that opened it.
  useEffect(() => {
    if (!cycling) return;
    return kernel.bus.on('shell:switcherAdvance', ({ delta }) => {
      setSelected((index) => moveInGrid(index, windows.length, SWITCHER_COLUMNS, delta > 0 ? 'ArrowRight' : 'ArrowLeft'));
    });
  }, [cycling, kernel, windows.length]);

  /**
   * Releasing the held modifier commits. Which modifier that is comes from the *keymap*, not
   * from a hard-coded `Alt`: the macOS skin (M11) binds ⌘+Tab, and this has to follow it.
   */
  useEffect(() => {
    if (!cycling) return;
    const held = modifiersOf(kernel.keyboard.chordFor('switcher.windows') ?? '');
    if (held.length === 0) return;
    const onKeyUp = (event: KeyboardEvent): void => {
      if (!held.includes(event.key)) return;
      pending.current.commit(pending.current.selected);
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [cycling, kernel]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.key;
      if (isGridKey(key)) {
        event.preventDefault();
        setSelected((index) => moveInGrid(index, windows.length, SWITCHER_COLUMNS, key));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(selected);
      }
      // Escape is the shell's, not the skin's: `Desktop` closes whatever overlay is open,
      // wherever focus happens to be, and closing changes no focus.
    },
    [commit, selected, windows.length],
  );

  if (!open) return null;

  return (
    <div
      className="house-switcher"
      data-testid="switcher"
      data-mode={cycling ? 'cycle' : 'overview'}
      role="dialog"
      aria-label={cycling ? 'Switch window' : 'Task View'}
      tabIndex={-1}
      ref={surface}
      onKeyDown={onKeyDown}
      onClick={() => kernel.shellui.closeOverlay()}
    >
      <div
        className="house-switcher-grid"
        role="listbox"
        aria-label="Open windows"
        data-testid="switcher-grid"
        onClick={(event) => event.stopPropagation()}
      >
        {windows.map((win, index) => (
          <button
            key={win.id}
            type="button"
            role="option"
            aria-selected={index === selected}
            className="house-switcher-tile"
            data-testid={`switcher-tile-${win.id}`}
            data-selected={String(index === selected)}
            onPointerEnter={() => setSelected(index)}
            onClick={() => commit(index)}
          >
            <span className="house-switcher-thumb" aria-hidden="true" />
            <span className="house-switcher-label">{win.title}</span>
          </button>
        ))}
        {windows.length === 0 ? (
          <p className="house-switcher-empty" data-testid="switcher-empty">
            No open windows
          </p>
        ) : null}
      </div>

      {/* §5.5 — the desktop strip. Switching from here is a class toggle, never a DOM move. */}
      <div
        className="house-switcher-desktops"
        role="tablist"
        aria-label="Desktops"
        data-testid="switcher-desktops"
        onClick={(event) => event.stopPropagation()}
      >
        {desktops.map((desktop) => (
          <button
            key={desktop.id}
            type="button"
            role="tab"
            aria-selected={desktop.id === activeDesktop}
            className="house-switcher-desktop"
            data-testid={`switcher-desktop-${desktop.id}`}
            data-active={String(desktop.id === activeDesktop)}
            onClick={() => {
              kernel.desktops.setActive(desktop.id);
              setSelected(0);
            }}
          >
            {desktop.name}
          </button>
        ))}
        <button
          type="button"
          className="house-switcher-desktop house-switcher-add"
          aria-label="New desktop"
          data-testid="switcher-desktop-new"
          onClick={() => kernel.desktops.create()}
        >
          +
        </button>
      </div>
    </div>
  );
}
